import { Touchable, useStateWithRef, Video, destroyMediaStream } from "@kalyzee/kast-app-web-components";
import { OvenMediaEmitterWebRTCSession, OvenMediaEmitterWebRTCSessionOptions, OvenMediaReceiverWebRTCSession, OvenMediaReceiverWebRTCSessionOptions } from "@kalyzee/kast-webrtc-client-module";
import { useEffect, useMemo, useRef, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import PageContainer from "../../components/page/PageContainer";
import { VHOST } from "../../constants/ovenmedia";
import { getOvenMediaPushWebsocketUrl, getOvenMediaPullWebsocketUrl } from "../../helpers/request";
import { addToPathNameUrl } from "../../helpers/utils";
import { OvenMediaContext } from "../../interfaces/context";
import { useOvenMediaContexts } from "../../store/context/hooks";
import { useMultiServer } from "../../store/session/hooks";
import styles from "./streaming.module.css";
import { getSelectedConfig } from "../../helpers/storage";
import { Stats } from "../player/stats";

type ConnectionStatus = "idle" | "connecting" | "connected" | "error";

// ---- RECEPTION ---- //

const ReceptionSection = ({ context, applications }: { context: OvenMediaContext; applications: string[] }) => {
  const [app, setApp] = useState<string>(applications[0] ?? "");
  const [stream, setStream] = useState<string>("test");
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [media, setMedia] = useState<MediaStream | undefined>(undefined);
  const sessionRef = useRef<OvenMediaReceiverWebRTCSession | undefined>(undefined);
  const [configText, setConfigText] = useState<string>(JSON.stringify(getSelectedConfig()?.rtcOptions ?? {} as OvenMediaReceiverWebRTCSessionOptions, null, 2));
  const [configError, setConfigError] = useState<string>("");

  useEffect(() => {
    if (applications.length && !applications.includes(app)) {
      setApp(applications[0]);
    }
  }, [applications]);

  const parseConfig = (): OvenMediaReceiverWebRTCSessionOptions | undefined => {
    try {
      const parsed = JSON.parse(configText);
      setConfigError("");
      return parsed;
    } catch {
      setConfigError("JSON invalide");
      return undefined;
    }
  };

  const start = () => {
    if (!app || !stream) return;
    const options = parseConfig();
    if (options === undefined) return;
    stop();
    setStatus("connecting");
    setErrorMsg("");

    const baseUrl = getOvenMediaPullWebsocketUrl(context);
    if (!baseUrl) {
      setStatus("error");
      setErrorMsg("URL de réception non configurée");
      return;
    }
    const url = addToPathNameUrl(baseUrl, `/${app}/${stream}`);
    const session = new OvenMediaReceiverWebRTCSession(options);
    sessionRef.current = session;
    session.start(url);

    const timeout = setTimeout(() => {
      if (status === "connecting") {
        setStatus("error");
        setErrorMsg("Timeout de connexion");
        session.destroy();
      }
    }, 10000);

    session.addEventListener("stream", (s: MediaStream) => {
      clearTimeout(timeout);
      setMedia(s);
      console.log("Media : ", s);
      setStatus("connected");
    });
    session.addEventListener("close", () => {
      clearTimeout(timeout);
      setMedia(undefined);
      setStatus("error");
      setErrorMsg("Reconnexion...");
    });
  };

  const stop = () => {
    sessionRef.current?.destroy();
    sessionRef.current = undefined;
    if (media) destroyMediaStream(media);
    setMedia(undefined);
    setStatus("idle");
    setErrorMsg("");
  };

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
    };
  }, []);

  const isActive = status !== "idle";

  const generatedUrl = useMemo(() => {
    const baseUrl = getOvenMediaPullWebsocketUrl(context);
    if (!baseUrl || !app || !stream) return undefined;
    return addToPathNameUrl(baseUrl, `/${app}/${stream}`);
  }, [context, app, stream]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{"Réception" /* TRANSLATION */}</div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>{"Application :" /* TRANSLATION */}</span>
        <select
          className={styles.formSelect}
          value={app}
          onChange={(e) => setApp(e.target.value)}
          disabled={isActive}
        >
          {applications.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>{"Stream :" /* TRANSLATION */}</span>
        <input
          className={styles.formInput}
          type="text"
          placeholder="Nom du stream"
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          disabled={isActive}
        />
      </div>
      {generatedUrl && <div className={styles.generatedUrl}>{generatedUrl}</div>}
      <details className={styles.configDetails}>
        <summary className={styles.configSummary}>{"Options" /* TRANSLATION */}</summary>
        <textarea
          className={styles.formTextarea}
          value={configText}
          onChange={(e) => { setConfigText(e.target.value); setConfigError(""); }}
          disabled={isActive}
          spellCheck={false}
        />
        {configError && <div className={styles.configError}>{configError}</div>}
      </details>
      <div className={styles.formRow}>
        {!isActive ? (
          <Touchable
            className={`${styles.startButton} ${!app || !stream ? styles.buttonDisabled : ''}`}
            onPress={start}
            disabled={!app || !stream}
          >
            {"Lire" /* TRANSLATION */}
          </Touchable>
        ) : (
          <Touchable className={styles.stopButton} onPress={stop}>
            {"Arrêter" /* TRANSLATION */}
          </Touchable>
        )}
        {status === "connecting" && <span className={styles.statusText}>{"Connexion en cours..." /* TRANSLATION */}</span>}
        {status === "connected" && <span className={`${styles.statusText} ${styles.statusConnected}`}>{"Connecté" /* TRANSLATION */}</span>}
        {status === "error" && <span className={`${styles.statusText} ${styles.statusError}`}>{errorMsg}</span>}
      </div>
      {(media || isActive) && (
        <div className={styles.videoContainer}>
          <Video autoPlay muted={false} controls src={media} className={styles.video} />
        </div>
      )}
      {status === "connected" && <Stats session={sessionRef.current} />}
    </div>
  );
};

// ---- EMISSION ---- //

const EmissionSection = ({ context, applications }: { context: OvenMediaContext; applications: string[] }) => {
  const [app, setApp] = useState<string>(applications[0] ?? "");
  const [stream, setStream] = useState<string>("test");
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [localMedia, setLocalMedia] = useState<MediaStream | undefined>(undefined);
  const sessionRef = useRef<OvenMediaEmitterWebRTCSession | undefined>(undefined);
  const [configText, setConfigText] = useState<string>(JSON.stringify(getSelectedConfig()?.rtcOptions ?? {} as OvenMediaEmitterWebRTCSessionOptions, null, 2));
  const [configError, setConfigError] = useState<string>("");
  const [mediaSource, setMediaSource] = useState<"webcam" | "screenshare">("webcam");
  const [constraintsText, setConstraintsText] = useState<string>(JSON.stringify({ video: true, audio: true }, null, 2));
  const [constraintsError, setConstraintsError] = useState<string>("");
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");

  const enumerateDevices = async () => {
    try {
      // Request a temporary stream to get device labels
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => null);
      const devices = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
      setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
      if (tempStream) tempStream.getTracks().forEach((t) => t.stop());
    } catch {
      setVideoDevices([]);
      setAudioDevices([]);
    }
  };

  useEffect(() => {
    if (mediaSource === "webcam") enumerateDevices();
  }, [mediaSource]);

  useEffect(() => {
    if (applications.length && !applications.includes(app)) {
      setApp(applications[0]);
    }
  }, [applications]);

  const parseConfig = (): OvenMediaEmitterWebRTCSessionOptions | undefined => {
    try {
      const parsed = JSON.parse(configText);
      setConfigError("");
      return parsed;
    } catch {
      setConfigError("JSON invalide");
      return undefined;
    }
  };

  const parseConstraints = (): MediaStreamConstraints | undefined => {
    try {
      const parsed = JSON.parse(constraintsText);
      setConstraintsError("");
      return parsed;
    } catch {
      setConstraintsError("JSON invalide");
      return undefined;
    }
  };

  const start = async () => {
    if (!app || !stream) return;
    const options = parseConfig();
    if (options === undefined) return;
    const constraints = parseConstraints();
    if (constraints === undefined) return;
    stop();
    setStatus("connecting");
    setErrorMsg("");

    const baseUrl = getOvenMediaPushWebsocketUrl(context);
    if (!baseUrl) {
      setStatus("error");
      setErrorMsg("URL d'émission non configurée (pushWebMediaUrl)");
      return;
    }

    let mediaStream: MediaStream;
    try {
      if (mediaSource === "screenshare") {
        mediaStream = await navigator.mediaDevices.getDisplayMedia(constraints);
      } else {
        const webcamConstraints = { ...constraints };
        if (selectedVideoDevice && webcamConstraints.video !== false) {
          webcamConstraints.video = typeof webcamConstraints.video === "object"
            ? { ...webcamConstraints.video, deviceId: { exact: selectedVideoDevice } }
            : { deviceId: { exact: selectedVideoDevice } };
        }
        if (selectedAudioDevice && webcamConstraints.audio !== false) {
          webcamConstraints.audio = typeof webcamConstraints.audio === "object"
            ? { ...webcamConstraints.audio, deviceId: { exact: selectedAudioDevice } }
            : { deviceId: { exact: selectedAudioDevice } };
        }
        mediaStream = await navigator.mediaDevices.getUserMedia(webcamConstraints);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(mediaSource === "screenshare"
        ? "Impossible de capturer l'écran"
        : "Impossible d'accéder à la caméra/micro");
      return;
    }
    setLocalMedia(mediaStream);

    const url = addToPathNameUrl(baseUrl, `/${app}/${stream}`);
    const session = new OvenMediaEmitterWebRTCSession(options);
    sessionRef.current = session;

    try {
      const success = await session.start(url, mediaStream);
      if (success) {
        setStatus("connected");
      } else {
        setStatus("error");
        setErrorMsg("Échec de la connexion");
        destroyMediaStream(mediaStream);
        setLocalMedia(undefined);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Erreur lors de la connexion");
      destroyMediaStream(mediaStream);
      setLocalMedia(undefined);
    }

    session.addEventListener("close", () => {
      setStatus("error");
      setErrorMsg("Déconnecté");
    });
  };

  const stop = () => {
    sessionRef.current?.destroy();
    sessionRef.current = undefined;
    if (localMedia) {
      destroyMediaStream(localMedia);
      setLocalMedia(undefined);
    }
    setStatus("idle");
    setErrorMsg("");
  };

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
      if (localMedia) destroyMediaStream(localMedia);
    };
  }, []);

  const isActive = status === "connecting" || status === "connected";

  const generatedUrl = useMemo(() => {
    const baseUrl = getOvenMediaPushWebsocketUrl(context);
    if (!baseUrl || !app || !stream) return undefined;
    return addToPathNameUrl(baseUrl, `/${app}/${stream}`);
  }, [context, app, stream]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{"Émission" /* TRANSLATION */}</div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>{"Application :" /* TRANSLATION */}</span>
        <select
          className={styles.formSelect}
          value={app}
          onChange={(e) => setApp(e.target.value)}
          disabled={isActive}
        >
          {applications.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>{"Stream :" /* TRANSLATION */}</span>
        <input
          className={styles.formInput}
          type="text"
          placeholder="Nom du stream"
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          disabled={isActive}
        />
      </div>
      {generatedUrl && <div className={styles.generatedUrl}>{generatedUrl}</div>}
      <details className={styles.configDetails}>
        <summary className={styles.configSummary}>{"Options" /* TRANSLATION */}</summary>
        <textarea
          className={styles.formTextarea}
          value={configText}
          onChange={(e) => { setConfigText(e.target.value); setConfigError(""); }}
          disabled={isActive}
          spellCheck={false}
        />
        {configError && <div className={styles.configError}>{configError}</div>}
      </details>
      <details className={styles.configDetails}>
        <summary className={styles.configSummary}>{"Media constraints" /* TRANSLATION */}</summary>
        <textarea
          className={styles.formTextarea}
          value={constraintsText}
          onChange={(e) => { setConstraintsText(e.target.value); setConstraintsError(""); }}
          disabled={isActive}
          spellCheck={false}
        />
        {constraintsError && <div className={styles.configError}>{constraintsError}</div>}
      </details>
      <div className={styles.formRow}>
        <span className={styles.formLabel}>{"Source :" /* TRANSLATION */}</span>
        <div className={styles.switchContainer}>
          <Touchable
            className={`${styles.switchOption} ${mediaSource === "webcam" ? styles.switchOptionActive : ""}`}
            onPress={() => !isActive && setMediaSource("webcam")}
          >
            {"Webcam"}
          </Touchable>
          <Touchable
            className={`${styles.switchOption} ${mediaSource === "screenshare" ? styles.switchOptionActive : ""}`}
            onPress={() => !isActive && setMediaSource("screenshare")}
          >
            {"Screenshare"}
          </Touchable>
        </div>
      </div>
      {mediaSource === "webcam" && videoDevices.length > 0 && (
        <div className={styles.formRow}>
          <span className={styles.formLabel}>{"Caméra :" /* TRANSLATION */}</span>
          <select
            className={styles.formSelect}
            value={selectedVideoDevice}
            onChange={(e) => setSelectedVideoDevice(e.target.value)}
            disabled={isActive}
          >
            <option value="">{"Défaut" /* TRANSLATION */}</option>
            {videoDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>
            ))}
          </select>
        </div>
      )}
      {mediaSource === "webcam" && audioDevices.length > 0 && (
        <div className={styles.formRow}>
          <span className={styles.formLabel}>{"Micro :" /* TRANSLATION */}</span>
          <select
            className={styles.formSelect}
            value={selectedAudioDevice}
            onChange={(e) => setSelectedAudioDevice(e.target.value)}
            disabled={isActive}
          >
            <option value="">{"Défaut" /* TRANSLATION */}</option>
            {audioDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.formRow}>
        {!isActive ? (
          <Touchable
          className={`${styles.startButton} ${!app || !stream ? styles.buttonDisabled : ''}`}
            onPress={start}
            disabled={!app || !stream}
          >
            {"Diffuser" /* TRANSLATION */}
          </Touchable>
        ) : (
          <Touchable className={styles.stopButton} onPress={stop}>
            {"Arrêter" /* TRANSLATION */}
          </Touchable>
        )}
        {status === "connecting" && <span className={styles.statusText}>{"Connexion en cours..." /* TRANSLATION */}</span>}
        {status === "connected" && <span className={`${styles.statusText} ${styles.statusConnected}`}>{"Connecté" /* TRANSLATION */}</span>}
        {status === "error" && <span className={`${styles.statusText} ${styles.statusError}`}>{errorMsg}</span>}
      </div>
      {(localMedia || isActive) && (
        <div className={styles.videoContainer}>
          <Video autoPlay muted src={localMedia} className={styles.video} />
        </div>
      )}
      {status === "connected" && <Stats session={sessionRef.current} />}
    </div>
  );
};

// ---- PAGE ---- //

const StreamingPage = () => {
  const multiServer = useMultiServer();
  const contexts = useOvenMediaContexts();
  const [currContext, setCurrContext] = useStateWithRef<OvenMediaContext | undefined>();

  useEffect(() => {
    if (!multiServer) setCurrContext(contexts[0]);
  }, [contexts]);

  const applications = useMemo(() => {
    const apps = new Set<string>();
    contexts?.forEach((context) => {
      if (!context?.vhosts[VHOST]) return;
      Object.keys(context.vhosts[VHOST].apps)
        .sort((a, b) => a.localeCompare(b))
        .forEach((a) => apps.add(a));
    });
    return Array.from(apps);
  }, [contexts]);

  const renderContent = (context: OvenMediaContext) => {
    return (
      <div className={styles.content}>
        <ReceptionSection context={context} applications={applications} />
        <EmissionSection context={context} applications={applications} />
      </div>
    );
  };

  if (!contexts.length) return null;

  return (
    <PageContainer title={"Réception / Émission" /* TRANSLATION */} subtitle={""} loading={false}>
      {multiServer ? (
        <Tabs
          className={[styles.tabs].join(' ')}
          onSelect={(index) => {
            const context = index < contexts.length ? contexts[index] : undefined;
            setCurrContext(context);
          }}
        >
          <TabList>
            {contexts.map((c) => (
              <Tab key={`streaming_tab_${c.server._id}`}>{`Server ${c.server._id}`}</Tab>
            ))}
          </TabList>
          {contexts.map((c) => (
            <TabPanel className={[styles.tabsContent, "react-tabs-content"].join(' ')} key={`streaming_content_${c.server._id}`}>
              {renderContent(c)}
            </TabPanel>
          ))}
        </Tabs>
      ) : (
        renderContent(contexts[0])
      )}
    </PageContainer>
  );
};

export default StreamingPage;
